import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { MapPin, Hotel, Building2, Plane, Clock, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { recentSearchesStorage } from "@/utils/localStorage";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import { Flag } from "@/utils/flag";
import { COUNTRIES } from "@/utils/countries";

export type Suggestion = {
  type: "country" | "city" | "region" | "hotel";
  name: string;
  regionId?: number;
  hotelId?: string;
  regionType?: string;
  countryCode?: string;
  starRating?: number;
  hotelCount?: number;
  cityName?: string;
};

const countryCodeToName: Record<string, string> = {};
COUNTRIES.forEach((c) => {
  countryCodeToName[c.code] = c.label;
});

function shortCountry(code?: string): string | null {
  if (!code) return null;
  const SHORT: Record<string, string> = {
    AE: "UAE",
    US: "USA",
    GB: "UK",
    KR: "South Korea",
    KP: "North Korea",
    CD: "DR Congo",
    BA: "Bosnia",
    VC: "St. Vincent",
    KN: "St. Kitts",
    LC: "St. Lucia",
    TT: "Trinidad",
    AG: "Antigua",
    DO: "Dominican Rep.",
    PG: "Papua NG",
    MK: "N. Macedonia",
  };
  const upper = code.toUpperCase();
  return SHORT[upper] || countryCodeToName[upper] || null;
}

function formatHotelCount(count?: number): string | null {
  if (!count || count < 50) return null;
  if (count >= 1000) return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}k properties`;
  return `${count} properties`;
}

/** Map ETG region types to concise labels + icons */
function getTypeInfo(regionType?: string): { label: string; icon: typeof MapPin } {
  const t = (regionType || "").toLowerCase();
  if (t.includes("airport")) return { label: "Airport", icon: Plane };
  if (t === "city") return { label: "City", icon: Building2 };
  if (t === "country") return { label: "Country", icon: MapPin };
  if (t.includes("neighborhood") || t.includes("district")) return { label: "Area", icon: MapPin };
  if (t.includes("province") || t.includes("state")) return { label: "Region", icon: MapPin };
  return { label: "Destination", icon: MapPin };
}

/** Strip diacritics/accents: "Mövenpick" → "Movenpick" */
function stripDiacritics(str: string): string {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

interface SearchAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onSelectSuggestion?: (s: Suggestion) => void;
  /** When true, don't auto-open the dropdown on mount even if value is pre-filled */
  suppressInitialOpen?: boolean;
}


// All destination data comes from the database via offline-autocomplete edge function.
// No hardcoded city/region data — eliminates regionId mismatch bugs.

// In-memory cache for autocomplete results (survives re-renders, shared across instances)
const autocompleteCache = new Map<string, { destinations: Suggestion[]; hotels: Suggestion[]; ts: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const CACHE_MAX = 80;

function getCached(key: string) {
  const entry = autocompleteCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL) {
    autocompleteCache.delete(key);
    return null;
  }
  return entry;
}
function setCache(key: string, destinations: Suggestion[], hotels: Suggestion[]) {
  if (autocompleteCache.size >= CACHE_MAX) {
    const first = autocompleteCache.keys().next().value;
    if (first) autocompleteCache.delete(first);
  }
  autocompleteCache.set(key, { destinations, hotels, ts: Date.now() });
}

export const SearchAutocomplete = ({
  value,
  onChange,
  placeholder,
  onSelectSuggestion,
  suppressInitialOpen = false,
}: SearchAutocompleteProps) => {
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [destinations, setDestinations] = useState<Suggestion[]>([]);
  const [hotels, setHotels] = useState<Suggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    hasSpaceBelow: true,
    viewportTop: 0,
  });
  const lastGoodQueryRef = useRef("");
  const lastGoodHotelsRef = useRef<Suggestion[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const justSelectedRef = useRef(false);
  const lastQueryRef = useRef("");
  const inflightQueryRef = useRef("");
  const hasUserInteractedRef = useRef(!suppressInitialOpen ? false : true);
  // When suppressInitialOpen, skip showing dropdown until user focuses/types
  const mountedWithValueRef = useRef(suppressInitialOpen && value.length > 0);

  // Stable reference to runSearch to avoid recreating debounced callback
  const runSearchRef = useRef<(q: string) => void>(() => {});

  const debouncedSearch = useDebouncedCallback((searchValue: string) => {
    const trimmed = searchValue.toLowerCase().trim();
    // Skip if same as last completed or currently in-flight query
    if (trimmed === lastQueryRef.current || trimmed === inflightQueryRef.current) return;
    if (searchValue.length >= 2) {
      runSearchRef.current(searchValue);
    }
  }, 300);

  useEffect(() => {
    if (justSelectedRef.current) return;
    // Skip auto-opening on initial mount when value is pre-filled
    if (mountedWithValueRef.current) {
      mountedWithValueRef.current = false;
      return;
    }
    if (value.length >= 1) {
      const cacheKey = value.toLowerCase().trim();
      const cached = getCached(cacheKey);

      if (cached) {
        setDestinations(cached.destinations);
        setHotels(cached.hotels);
        setShowResults(true);
        setSelectedIndex(-1);
      } else {
        // No cached results — wait for API response (no hardcoded predictions)
      }

      if (value.length >= 2) {
        debouncedSearch(value);
      }
    } else if (value.length === 0) {
      setShowResults(false);
      setDestinations([]);
      setHotels([]);
    }
  }, [value, debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (showResults && containerRef.current) {
      const updatePosition = () => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          hasSpaceBelow: spaceBelow > 200,
          viewportTop: rect.bottom,
        });
      };
      updatePosition();
      // Update position on scroll instead of closing — dropdown uses position:fixed
      window.addEventListener("scroll", updatePosition, { passive: true });
      window.addEventListener("resize", updatePosition, { passive: true });
      if (window.innerWidth < 768) {
        window.visualViewport?.addEventListener("resize", updatePosition);
        window.visualViewport?.addEventListener("scroll", updatePosition);
      }
      return () => {
        window.removeEventListener("scroll", updatePosition);
        window.removeEventListener("resize", updatePosition);
        window.visualViewport?.removeEventListener("resize", updatePosition);
        window.visualViewport?.removeEventListener("scroll", updatePosition);
      };
    }
  }, [showResults]);

  // Country aliases to skip hotel VM search (offline-autocomplete handles these)
  const COUNTRY_SHORTCUTS = new Set([
    "usa",
    "us",
    "united states",
    "uae",
    "ae",
    "emirates",
    "uk",
    "gb",
    "britain",
    "ksa",
    "sa",
    "saudi",
    "qatar",
    "qa",
    "bahrain",
    "bh",
    "kuwait",
    "kw",
    "oman",
    "om",
    "egypt",
    "eg",
    "jordan",
    "jo",
    "lebanon",
    "lb",
    "iraq",
    "iq",
    "morocco",
    "ma",
    "tunisia",
    "tn",
    "turkey",
    "tr",
    "india",
    "in",
    "china",
    "cn",
    "japan",
    "jp",
    "korea",
    "kr",
    "thailand",
    "th",
    "singapore",
    "sg",
    "malaysia",
    "my",
    "indonesia",
    "id",
    "philippines",
    "ph",
    "vietnam",
    "vn",
    "australia",
    "au",
    "new zealand",
    "nz",
    "canada",
    "ca",
    "mexico",
    "mx",
    "brazil",
    "br",
    "argentina",
    "ar",
    "france",
    "fr",
    "germany",
    "de",
    "italy",
    "it",
    "spain",
    "es",
    "portugal",
    "pt",
    "netherlands",
    "nl",
    "holland",
    "belgium",
    "be",
    "switzerland",
    "ch",
    "austria",
    "at",
    "sweden",
    "se",
    "norway",
    "no",
    "denmark",
    "dk",
    "finland",
    "fi",
    "poland",
    "pl",
    "greece",
    "gr",
    "ireland",
    "ie",
    "russia",
    "ru",
    "ukraine",
    "ua",
    "south africa",
    "za",
    "nigeria",
    "ng",
    "kenya",
    "ke",
    "ghana",
    "gh",
    "tanzania",
    "tz",
    "ethiopia",
    "et",
    "colombia",
    "co",
    "chile",
    "cl",
    "peru",
    "pe",
    "czech",
    "cz",
    "romania",
    "ro",
    "hungary",
    "hu",
    "croatia",
    "hr",
    "serbia",
    "rs",
    "bulgaria",
    "bg",
    "slovakia",
    "sk",
    "slovenia",
    "si",
    "estonia",
    "ee",
    "latvia",
    "lv",
    "lithuania",
    "lt",
    "iceland",
    "is",
    "luxembourg",
    "lu",
    "malta",
    "mt",
    "cyprus",
    "cy",
    "pakistan",
    "pk",
    "bangladesh",
    "bd",
    "sri lanka",
    "lk",
    "nepal",
    "np",
    "myanmar",
    "mm",
    "cambodia",
    "kh",
    "laos",
    "la",
    "mongolia",
    "mn",
    "uzbekistan",
    "uz",
    "kazakhstan",
    "kz",
    "georgia",
    "ge",
    "armenia",
    "am",
    "azerbaijan",
    "az",
    "israel",
    "il",
    "palestine",
    "ps",
    "iran",
    "ir",
    "maldives",
    "mv",
    "fiji",
    "fj",
    "jamaica",
    "jm",
    "cuba",
    "cu",
    "haiti",
    "ht",
    "panama",
    "pa",
    "costa rica",
    "cr",
    "puerto rico",
    "pr",
    "bermuda",
    "bm",
    "bahamas",
    "bs",
    "barbados",
    "bb",
    "trinidad",
    "tt",
    "monaco",
    "mc",
    "liechtenstein",
    "li",
    "andorra",
    "ad",
    "san marino",
    "sm",
  ]);

  const runSearch = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setDestinations([]);
      setHotels([]);
      setShowResults(query.length > 0);
      return;
    }

    const cacheKey = query.toLowerCase().trim();

    // ⚡ Instant cache hit — no network, no loading spinner
    const cached = getCached(cacheKey);
    if (cached) {
      setDestinations(cached.destinations);
      setHotels(cached.hotels);
      setShowResults(true);
      setSelectedIndex(-1);
      setLoading(false);
      return;
    }

    // Prevent duplicate in-flight requests
    if (inflightQueryRef.current === cacheKey) return;
    inflightQueryRef.current = cacheKey;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    const isCountryQuery = COUNTRY_SHORTCUTS.has(cacheKey);

    // Detect comma-separated search: "marriott, riyadh" → hotel=marriott, location=riyadh
    const commaIdx = query.indexOf(",");
    const isCommaSearch = commaIdx > 0 && commaIdx < query.length - 1;
    const hotelPart = isCommaSearch ? query.slice(0, commaIdx).trim() : null;
    const locationPart = isCommaSearch ? query.slice(commaIdx + 1).trim() : null;

    setLoading(true);
    try {
      // --- Comma search: look up region first, then search hotels filtered by region ---
      if (isCommaSearch && hotelPart && locationPart && locationPart.length >= 2) {
        const normalizedLocation = stripDiacritics(locationPart);
        const strippedHotel = stripDiacritics(hotelPart);
        const hasAccents = strippedHotel !== hotelPart;

        // Parallel: fetch region IDs + hotel results from VM
        // Send both original (with diacritics) AND stripped queries because
        // the VM treats "Mövenpick" and "Movenpick" as completely different searches
        const regionPromise = supabase
          .from("regions")
          .select("id, name, type, country_code")
          .ilike("name", `${normalizedLocation}%`)
          .order("hotel_count", { ascending: false, nullsFirst: false })
          .limit(5);
        const vmPromise1 = supabase.functions.invoke("exora-search", {
          body: { kind: "hotels", q: hotelPart.trim(), limit: 50 },
        });
        const vmPromise2 = hasAccents
          ? supabase.functions.invoke("exora-search", {
              body: { kind: "hotels", q: strippedHotel.trim(), limit: 50 },
            })
          : Promise.resolve({ data: { results: [] }, error: null });

        const [regionLookup, vmResult1, vmResult2] = await Promise.all([regionPromise, vmPromise1, vmPromise2]);

        if (inflightQueryRef.current !== cacheKey) return;

        const matchedRegions = regionLookup.data || [];
        const matchedRegionIds = new Set(matchedRegions.map((r) => String(r.id)));

        // Merge VM results from both queries
        const vmList1 = Array.isArray(vmResult1.data?.results) ? vmResult1.data.results : [];
        const vmList2 = vmResult2 && Array.isArray(vmResult2.data?.results) ? vmResult2.data.results : [];
        const seenHids = new Set<number>();
        const mergedVmHotels: any[] = [];
        for (const h of [...vmList1, ...vmList2]) {
          if (!seenHids.has(h.hid)) {
            seenHids.add(h.hid);
            mergedVmHotels.push(h);
          }
        }

        // Filter to matched region(s) AND match all hotel name words
        const hotelWords = strippedHotel
          .toLowerCase()
          .split(/\s+/)
          .filter((w) => w.length > 0);
        const filteredVmHotels: Suggestion[] = mergedVmHotels
          .filter((h: any) => {
            if (!h.region_id || !matchedRegionIds.has(String(h.region_id))) return false;
            // Check all words from the hotel part match somewhere in the name
            const nameLower = stripDiacritics(h.name).toLowerCase();
            return hotelWords.every((w: string) => nameLower.includes(w));
          })
          .map((h: any) => ({
            type: "hotel" as const,
            name: h.name,
            hotelId: String(h.hid),
            regionId: h.region_id,
            regionType: h.region_type,
            countryCode: h.country_code,
            starRating: h.star_rating,
            cityName: matchedRegions.find((r) => String(r.id) === String(h.region_id))?.name || undefined,
          }));

        // Show matched regions as destinations for context
        const locationDestinations: Suggestion[] = matchedRegions.map((r: any) => ({
          type: ((r.type || "").toLowerCase() === "city" ? "city" : "region") as Suggestion["type"],
          name: r.name,
          regionId: Number(r.id),
          regionType: r.type,
          countryCode: r.country_code,
        }));

        setDestinations(locationDestinations);
        setHotels(filteredVmHotels);
        if (filteredVmHotels.length > 0) {
          lastGoodQueryRef.current = cacheKey;
          lastGoodHotelsRef.current = filteredVmHotels;
        }
        setCache(cacheKey, locationDestinations, filteredVmHotels);
        lastQueryRef.current = cacheKey;
        setShowResults(true);
        setSelectedIndex(-1);
        return;
      }

      // --- Standard search (no comma) ---
      // Always fetch regions; strip diacritics for consistent matching
      const normalizedQuery = stripDiacritics(query);
      const regionsPromise = supabase.functions.invoke("offline-autocomplete", {
        body: { query: normalizedQuery, language: "en" },
      });

      // Send the full query to the VM — also send stripped version if accents detected
      const hotelSearchQuery = query.trim();
      const strippedQuery = stripDiacritics(hotelSearchQuery);
      const queryHasAccents = strippedQuery !== hotelSearchQuery;

      // For country queries, skip the VM hotel search — the edge function returns country hotels
      const hotelsPromise1 = isCountryQuery
        ? Promise.resolve({ data: { results: [] }, error: null })
        : supabase.functions.invoke("exora-search", {
            body: { kind: "hotels", q: hotelSearchQuery, limit: 20 },
          });

      // Parallel diacritic search: send stripped version too if accents are present
      const hotelsPromise2 =
        !isCountryQuery && queryHasAccents
          ? supabase.functions.invoke("exora-search", {
              body: { kind: "hotels", q: strippedQuery, limit: 20 },
            })
          : Promise.resolve({ data: { results: [] }, error: null });

      const [regionsResult, hotelsResult1, hotelsResult2] = await Promise.all([
        regionsPromise,
        hotelsPromise1,
        hotelsPromise2,
      ]);

      // If query changed while we were fetching, discard stale results
      if (inflightQueryRef.current !== cacheKey) return;

      if (regionsResult.error) console.error("Regions autocomplete failed:", regionsResult.error);
      if (hotelsResult1.error) console.error("Hotels search failed:", hotelsResult1.error);

      const regionsList = Array.isArray(regionsResult.data?.regions) ? regionsResult.data.regions : [];
      const vmList1 = Array.isArray(hotelsResult1.data?.results) ? hotelsResult1.data.results : [];
      const vmList2 = Array.isArray(hotelsResult2.data?.results) ? hotelsResult2.data.results : [];
      const offlineHotelsList = Array.isArray(regionsResult.data?.hotels) ? regionsResult.data.hotels : [];

      // Merge and deduplicate VM results from both queries (original + stripped diacritics)
      const seenVmHids = new Set<number>();
      const vmHotelsList: any[] = [];
      for (const h of [...vmList1, ...vmList2]) {
        if (!seenVmHids.has(h.hid)) {
          seenVmHids.add(h.hid);
          vmHotelsList.push(h);
        }
      }

      const apiDestinations: Suggestion[] = regionsList.map((r: any) => {
        const normalizedType = (r.type || "").toLowerCase().trim();
        return {
          type: (normalizedType === "country"
            ? "country"
            : normalizedType === "city"
              ? "city"
              : "region") as Suggestion["type"],
          name: r.name,
          regionId: r.id,
          regionType: r.type,
          countryCode: r.country_code,
          hotelCount: r.hotel_count || 0,
        };
      });

      setDestinations(apiDestinations);

      const vmHotels: Suggestion[] = vmHotelsList.map((h: any) => ({
        type: "hotel" as const,
        name: h.name,
        hotelId: String(h.hid),
        regionId: h.region_id,
        regionType: h.region_type,
        countryCode: h.country_code,
        starRating: h.star_rating,
      }));

      // Resolve region_id → city name from our regions table
      const regionIds = vmHotels.map((h) => h.regionId).filter((id): id is number => id != null && id > 0);
      const uniqueRegionIds = [...new Set(regionIds)];

      if (uniqueRegionIds.length > 0) {
        const { data: regionRows } = await supabase
          .from("regions")
          .select("id, name")
          .in("id", uniqueRegionIds.map(String));

        if (regionRows && regionRows.length > 0) {
          const regionNameMap = new Map(regionRows.map((r) => [String(r.id), r.name]));
          for (const h of vmHotels) {
            if (h.regionId) {
              h.cityName = regionNameMap.get(String(h.regionId)) || undefined;
            }
          }
        }
      }

      const offlineHotels: Suggestion[] = offlineHotelsList.map((h: any) => ({
        type: "hotel" as const,
        name: h.name,
        hotelId: h.id,
        countryCode: h.country_code,
        starRating: h.star_rating,
      }));
      const allHotels = [...vmHotels, ...offlineHotels];

      const seenHotelIds = new Set<string>();
      const dedupedHotels = allHotels.filter((h) => {
        if (!h.hotelId || seenHotelIds.has(h.hotelId)) return false;
        seenHotelIds.add(h.hotelId);
        return true;
      });

      // Client-side filter: keep only hotels whose name contains ALL query words
      const queryWords = query.trim().split(/\s+/);
      const filterWords = queryWords.map((w) => stripDiacritics(w).toLowerCase());
      const filterHotelsByQuery = (hotelList: Suggestion[]) => {
        if (filterWords.length <= 1) return hotelList;
        return hotelList.filter((h) => {
          const nameLower = stripDiacritics(h.name).toLowerCase();
          return filterWords.every((w) => nameLower.includes(w));
        });
      };

      if (dedupedHotels.length > 0) {
        lastGoodQueryRef.current = hotelSearchQuery.toLowerCase();
        lastGoodHotelsRef.current = dedupedHotels;
        setHotels(filterHotelsByQuery(dedupedHotels));
      } else if (!isCountryQuery && lastGoodHotelsRef.current.length > 0) {
        const filtered = filterHotelsByQuery(lastGoodHotelsRef.current);
        setHotels(filtered);
      } else {
        setHotels([]);
      }

      const finalHotels =
        dedupedHotels.length > 0 ? filterHotelsByQuery(dedupedHotels) : filterHotelsByQuery(lastGoodHotelsRef.current);
      setCache(cacheKey, apiDestinations, finalHotels);
      lastQueryRef.current = cacheKey;

      setShowResults(true);
      setSelectedIndex(-1);
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      console.error("Autocomplete failed:", e);
      setDestinations([]);
      setHotels([]);
      setShowResults(false);
    } finally {
      if (inflightQueryRef.current === cacheKey) {
        inflightQueryRef.current = "";
      }
      setLoading(false);
    }
  }, []); // No deps — uses only refs and setState

  // Keep runSearchRef in sync
  useEffect(() => {
    runSearchRef.current = runSearch;
  }, [runSearch]);

  const handleSearch = (query: string) => {
    onChange(query);
    setSelectedIndex(-1);
  };

  const allSuggestions = [
    ...destinations.map((s) => ({ ...s, category: "Destinations" })),
    ...hotels.map((s) => ({ ...s, category: "Hotels" })),
  ];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showResults || allSuggestions.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < allSuggestions.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < allSuggestions.length) {
          handleSelect(allSuggestions[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowResults(false);
        inputRef.current?.blur();
        break;
    }
  };

  const handleSelect = (s: Suggestion) => {
    justSelectedRef.current = true;
    const country = shortCountry(s.countryCode);
    const displayName = country && s.type !== "country" ? `${s.name}, ${country}` : s.name;
    onChange(displayName);
    onSelectSuggestion?.(s);
    setShowResults(false);
    recentSearchesStorage.add(displayName);
    setRecentSearches(recentSearchesStorage.getAll());
    setTimeout(() => {
      justSelectedRef.current = false;
    }, 100);
  };

  const clearRecent = () => {
    recentSearchesStorage.clear();
    setRecentSearches([]);
  };

  const hasResults = destinations.length > 0 || hotels.length > 0;

  // Generate star dots for hotel rating
  const renderStars = (rating: number) => {
    return (
      <span className="flex items-center gap-0.5">
        {Array.from({ length: rating }, (_, i) => (
          <Star key={i} className="w-2.5 h-2.5 text-gold/70 fill-gold/70" />
        ))}
      </span>
    );
  };

  return (
    <div className="relative w-full" ref={containerRef} style={{ position: "relative", overflow: "visible" }}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => value.length > 0 && setShowResults(true)}
          onKeyDown={handleKeyDown}
          className="w-full border-0 bg-transparent text-base md:text-sm text-ivory placeholder:text-muted-foreground focus:outline-none"
          aria-label="Search destination"
          role="combobox"
          aria-expanded={showResults}
          aria-autocomplete="list"
          autoComplete="on"
          autoCorrect="on"
          autoCapitalize="words"
          inputMode="search"
        />
        {loading && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <div className="relative w-3.5 h-3.5">
              <div className="absolute inset-0 rounded-full border border-gold/20" />
              <div className="absolute inset-0 rounded-full border border-gold border-t-transparent animate-spin" />
            </div>
          </div>
        )}
      </div>

      {showResults &&
        containerRef.current?.offsetParent !== null &&
        (value.length > 0 || recentSearches.length > 0) &&
        createPortal(
          <div
            ref={dropdownRef}
            className="search-autocomplete-dropdown z-[70] animate-fade-in"
            style={{
              ...(window.innerWidth < 768
                ? {
                    position: "absolute",
                    top: `max(${dropdownPosition.top + 8}px, calc(env(safe-area-inset-top) + 80px))`,
                    left: "max(12px, env(safe-area-inset-left))",
                    right: "max(12px, env(safe-area-inset-right))",
                    maxHeight: "calc(60vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
                  }
                : {
                    position: "fixed",
                    top: dropdownPosition.viewportTop + 12,
                    left: Math.max(16, dropdownPosition.left - window.scrollX - 24),
                    width: "min(480px, calc(100vw - 48px))",
                    maxHeight: "420px",
                  }),
            }}
            role="listbox"
          >
            {/* Glass container */}
            <div
              className="overflow-hidden rounded-2xl"
              style={{
                background: "linear-gradient(180deg, hsl(0 0% 5%) 0%, hsl(0 0% 3%) 100%)",
                border: "1px solid hsl(var(--gold) / 0.12)",
                boxShadow:
                  "0 24px 64px -12px rgba(0,0,0,0.85), 0 0 0 1px rgba(0,0,0,0.5), inset 0 1px 0 hsl(var(--gold) / 0.04)",
              }}
            >
              {/* Inner scrollable area */}
              <div className="overflow-y-auto max-h-[380px] md:max-h-[400px] gold-scrollbar">
                {/* Recent searches */}
                {value.length === 0 && recentSearches.length > 0 && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-ivory/30" />
                        <span className="text-[10px] font-medium text-ivory/40 uppercase tracking-[0.16em]">
                          Recent
                        </span>
                      </div>
                      <button
                        onClick={clearRecent}
                        className="text-[10px] text-ivory/30 hover:text-gold transition-colors tracking-wide"
                      >
                        Clear
                      </button>
                    </div>
                    {recentSearches.map((search, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSelect({ type: "city", name: search })}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-ivory/[0.04] text-ivory/60 hover:text-ivory transition-colors text-sm"
                      >
                        {search}
                      </button>
                    ))}
                  </div>
                )}

                {/* Minimum characters hint */}
                {value.length === 1 && (
                  <div className="py-10 text-center">
                    <p className="text-ivory/30 text-xs tracking-wide">Continue typing to search</p>
                  </div>
                )}

                {/* Loading skeleton — only show if we have NO destinations yet */}
                {loading && value.length > 1 && destinations.length === 0 && (
                  <div className="p-4 space-y-1">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2.5 animate-pulse">
                        <div className="w-5 h-4 rounded bg-ivory/[0.06]" />
                        <div className="h-3.5 rounded bg-ivory/[0.06]" style={{ width: `${50 + i * 20}%` }} />
                      </div>
                    ))}
                  </div>
                )}

                {value.length > 1 && (
                  <>
                    {/* Destinations section */}
                    {destinations.length > 0 && (
                      <div className="pt-3 pb-1">
                        <div className="px-5 mb-2">
                          <span className="text-[9px] font-semibold text-gold/40 uppercase tracking-[0.2em]">
                            Destinations
                          </span>
                        </div>
                        {destinations.map((result, idx) => {
                          const country = shortCountry(result.countryCode);
                          const hotelCountText = formatHotelCount(result.hotelCount);
                          const isActive = selectedIndex === idx;
                          const typeInfo = getTypeInfo(result.regionType);

                          return (
                            <button
                              key={`dest-${idx}`}
                              onClick={() => handleSelect(result)}
                              className={`group w-full text-left px-4 py-2.5 flex items-center gap-3 transition-all duration-100 ${
                                isActive ? "bg-gold/[0.08]" : "hover:bg-ivory/[0.03]"
                              }`}
                              role="option"
                              aria-selected={isActive}
                            >
                              {/* Flag or icon */}
                              <div className="w-7 h-5 flex items-center justify-center flex-shrink-0">
                                {result.countryCode ? (
                                  <Flag code={result.countryCode} size={20} className="rounded-[3px]" />
                                ) : (
                                  <typeInfo.icon className="w-4 h-4 text-ivory/25" />
                                )}
                              </div>

                              {/* Name + country */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-1.5">
                                  <span
                                    className={`text-[13px] font-medium truncate ${isActive ? "text-gold" : "text-ivory/90 group-hover:text-ivory"}`}
                                  >
                                    {result.name}
                                  </span>
                                  {country && result.type !== "country" && (
                                    <span className="text-[11px] text-ivory/30 flex-shrink-0">{country}</span>
                                  )}
                                </div>
                                {hotelCountText && (
                                  <span className="text-[10px] text-ivory/20 block mt-px">{hotelCountText}</span>
                                )}
                              </div>

                              {/* Type badge */}
                              <span className="text-[9px] text-ivory/20 uppercase tracking-wider flex-shrink-0">
                                {typeInfo.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Divider */}
                    {destinations.length > 0 && hotels.length > 0 && (
                      <div className="mx-5 border-t border-ivory/[0.06]" />
                    )}

                    {/* Hotels loading indicator */}
                    {loading && destinations.length > 0 && hotels.length === 0 && (
                      <div className="pt-3 pb-2">
                        <div className="px-5 mb-2">
                          <span className="text-[9px] font-semibold text-gold/40 uppercase tracking-[0.2em]">
                            Properties
                          </span>
                        </div>
                        <div className="px-4 space-y-1">
                          {[1, 2].map((i) => (
                            <div key={i} className="flex items-center gap-3 px-3 py-2.5 animate-pulse">
                              <div className="w-5 h-4 rounded bg-ivory/[0.06]" />
                              <div className="h-3.5 rounded bg-ivory/[0.06]" style={{ width: `${40 + i * 20}%` }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hotels section */}
                    {hotels.length > 0 && (
                      <div className="pt-3 pb-1">
                        <div className="px-5 mb-2">
                          <span className="text-[9px] font-semibold text-gold/40 uppercase tracking-[0.2em]">
                            Properties
                          </span>
                        </div>
                        {hotels.map((result, idx) => {
                          const globalIdx = destinations.length + idx;
                          const country = shortCountry(result.countryCode);
                          const isActive = selectedIndex === globalIdx;

                          return (
                            <button
                              key={`hotel-${idx}`}
                              onClick={() => handleSelect(result)}
                              className={`group w-full text-left px-4 py-2.5 flex items-center gap-3 transition-all duration-100 ${
                                isActive ? "bg-gold/[0.08]" : "hover:bg-ivory/[0.03]"
                              }`}
                              role="option"
                              aria-selected={isActive}
                            >
                              {/* Flag or hotel icon */}
                              <div className="w-7 h-5 flex items-center justify-center flex-shrink-0">
                                {result.countryCode ? (
                                  <Flag code={result.countryCode} size={18} className="rounded-[3px]" />
                                ) : (
                                  <Hotel className="w-4 h-4 text-ivory/25" />
                                )}
                              </div>

                              {/* Name + location */}
                              <div className="flex-1 min-w-0">
                                <span
                                  className={`text-[13px] font-medium truncate block ${isActive ? "text-gold" : "text-ivory/85 group-hover:text-ivory"}`}
                                >
                                  {result.name}
                                </span>
                                {(result.cityName || country || result.regionType) && (
                                  <span className="text-[10px] text-ivory/20 block mt-px">
                                    {[
                                      result.cityName,
                                      country,
                                      result.regionType && result.regionType !== "City" ? result.regionType : null,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </span>
                                )}
                              </div>

                              {/* Star rating */}
                              {typeof result.starRating === "number" && result.starRating >= 1 && (
                                <div className="flex-shrink-0">{renderStars(result.starRating)}</div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Empty state */}
                    {!loading && !hasResults && (
                      <div className="py-12 text-center">
                        <p className="text-ivory/25 text-xs tracking-wide mb-1">No destinations found</p>
                        <p className="text-ivory/15 text-[10px]">Try a different search term</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Bottom bar */}
              {!loading && hasResults && value.length > 1 && (
                <div className="px-5 py-2.5 border-t border-ivory/[0.04]">
                  <p className="text-[9px] text-ivory/15 text-center tracking-[0.12em] uppercase">
                    Exora Global Network
                  </p>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
